import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-logo-css.js';
import '@zywave/zui-svg';

/**
 * `<zui-logo>` allows for easy standardized use of our Zywave logos, and supports third-party logos if slotted in.
 *
 * @element zui-logo
 *
 * @slot - Default, unnamed slot; only use for inserting in a third-party logo into `<zui-logo>` to override the default Zywave logo options
 *
 * @cssprop [--zui-logo-primary-color=var(--zui-zywave, var(--zui-green))] - Logo primary colorization
 * @cssprop [--zui-logo-secondary-color=#8d8d8e] - Logo secondary colorization
 * @cssprop [--zui-logo-tertiary-color=#fff] - Logo tertiary colorization
 * @cssprop [--zui-logo-width=auto] - (slotted `<img>` elements): If neceessary, `<img>` width control
 * @cssprop [--zui-logo-height=auto] - (slotted `<img>` elements): If neceessary, `<img>` height control
 */
export class ZuiLogo extends ZuiBaseElement {
  /**
   * If using a Zywave logo, specify which type, if no type is specified it defaults to `logo` which should be used for third party logos
   * @type { 'tagline' | 'zycon' | 'zycon-circle' | 'logo' }
   */
  @property({ type: String, attribute: 'type' })
  type: ZuiLogoType = 'logo';

  static get styles() {
    return [super.styles, style];
  }

  render() {
    return html` <slot> <zui-svg svg-id="${this.computeLogo(this.type)}"></zui-svg> </slot> `;
  }

  private computeLogo(type: string): string {
    let logo = undefined;
    switch (type) {
      case 'tagline':
        logo = 'zywave-tagline';
        break;
      case 'zycon':
        logo = 'zycon';
        break;
      case 'zycon-circle':
        logo = 'zycon-circle';
        break;
      default:
        logo = 'zywave';
    }

    return logo;
  }
}

window.customElements.define('zui-logo', ZuiLogo);

export type ZuiLogoType = 'tagline' | 'zycon' | 'zycon-circle' | 'logo';

declare global {
  interface HTMLElementTagNameMap {
    'zui-logo': ZuiLogo;
  }
}
